/**
 * Types for OPA Standards Compliance feature
 * These types support the multi-framework compliance evaluation
 *
 * Supported Frameworks (Tier 1):
 * - CISA SCuBA: Secure Cloud Business Applications baseline for Microsoft 365
 * - NIST SP 800-53: Security and Privacy Controls
 * - NIST CSF: Cybersecurity Framework
 * - Microsoft MCSB: Microsoft Cloud Security Benchmark
 * - EIDSCA: Entra ID Security Configuration Analyzer
 * - Maester: Community-driven security tests
 */

// Supported compliance frameworks (Tier 1)
export const SUPPORTED_FRAMEWORKS = [
  'SCUBA',
  'NIST_800_53',
  'NIST_CSF',
  'MCSB',
  'EIDSCA',
  'MAESTER'
] as const
export type SupportedFramework = typeof SUPPORTED_FRAMEWORKS[number]

/**
 * Helper to determine if a framework code is a supported framework
 */
export function isSupportedFramework(code: string): boolean {
  return SUPPORTED_FRAMEWORKS.includes(code as SupportedFramework)
}

// Compliance standard/framework
export interface ComplianceStandard {
  id: string
  code: string                    // e.g., "SCUBA", "NIST_800_53", "EIDSCA"
  name: string                    // e.g., "CISA SCuBA M365", "NIST SP 800-53"
  description: string
  policyPackage: string           // OPA package: "scuba", "nist", "eidsca"
  totalControls: number
  isBuiltIn: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Individual policy within a standard
export interface CompliancePolicy {
  id: string
  standardId: string
  policyId: string                // e.g., "MS.AAD.1.1v1"
  title: string
  description: string
  criticality: PolicyCriticality
  weight: number
  category: string                // e.g., "Identity", "Exchange"
  product: string                 // e.g., "EntraID", "Exchange"
  frameworkReferences: string[]   // e.g., ["CISA SCuBA (MS.AAD.1.1v1)", "NIST 800-53 (AC-2)"]
  remediationSteps: string
  powerShellEquivalent?: string
  isActive: boolean
}

// Criticality levels matching CIPP standards
export type PolicyCriticality = 'SHALL' | 'SHOULD' | 'MAY'

// Waiver types for policy exceptions
export type WaiverType = 'Permanent' | 'Temporary' | 'NotApplicable'

// Violation status
export type ViolationStatus = 'Open' | 'Waived' | 'Resolved' | 'Pass' | 'DataUnavailable'

// Tenant's standard assignment with weights
export interface TenantStandardAssignment {
  id: string
  tenantId: string
  standardId: string
  standardCode: string
  standardName: string
  isEnabled: boolean
  weight: number                  // Backend: 0-100, displayed as importance 1-10
  includeInGuardianScore: boolean
  assignedAt: string
  assignedBy: string
}

// Policy waiver/exception
export interface TenantPolicyWaiver {
  id: string
  tenantId: string
  policyId: string
  policyCode: string
  policyTitle: string
  waiverType: WaiverType
  reason: string
  expiresAt?: string              // For Temporary waivers
  approvedBy: string
  approvedAt: string
  isActive: boolean
  /** True when the waiver's expiresAt is in the past. Server-computed on the
   *  WaiverInfoDto embedded in violation responses so the UI can show an
   *  "expired" state during the grace window between waiver expiry and the
   *  daily ProcessExpiredWaivers sweep. Optional because the standalone
   *  waiver-list endpoints currently don't populate it. */
  isExpired?: boolean
}

// Evaluation result
export interface ComplianceEvaluation {
  id: string
  tenantId: string
  standardId: string
  standardCode: string
  standardName: string
  evaluatedAt: string
  totalControls: number
  passedControls: number
  failedControls: number
  waivedControls: number
  rawScore: number                // Without waivers
  adjustedScore: number           // With waivers factored
  weightedScore: number           // Criticality-weighted
  evaluationDurationMs: number
}

// Individual violation
export interface ComplianceViolation {
  id: string
  evaluationId: string
  tenantId: string
  standardId: string              // The standard this violation belongs to
  policyId: string
  policyCode: string              // e.g., "MS.AAD.1.1v1"
  policyTitle: string
  status: ViolationStatus
  criticality: PolicyCriticality
  category: string
  product: string
  message: string
  details?: string
  diagnostics?: string
  affectedResource?: string
  frameworkReferences: string[]
  remediationSteps?: string
  remediationUrl?: string
  /**
   * Timestamp of the most recent scan that observed this violation. Reset on
   * every BPA evaluation (the per-standard pipeline deletes and re-inserts
   * rows). Treat as "last checked" / row freshness — for "first time we ever
   * saw this current open episode," prefer `firstDetectedAt`. Pre-migration
   * rows (before 2026-05-08) only have this field; the UI falls back to it
   * when `firstDetectedAt` is undefined.
   */
  detectedAt: string
  /**
   * When this current open episode first appeared. Held stable across rescans
   * by the backend, and reset only when a previously resolved policy starts
   * failing again. Surfaced as "First detected May 5" in the violation row.
   * Optional only because pre-migration violation rows don't have it; new
   * scans always populate it.
   */
  firstDetectedAt?: string
  resolvedAt?: string
  hasWaiver: boolean
  waiver?: TenantPolicyWaiver
  /** Estimated Guardian Score lift in percentage points if this violation is
   *  fixed. Null for unweighted policies / unstarred frameworks. Server-computed
   *  by ScoreDeltaCalculator and surfaced via the violations API. */
  guardianScoreDelta?: number | null
}

// Diagnostic output from Rego policy analysis
export interface PolicyCheckDiagnostics {
  type?: 'policyAnalysis'           // Optional for backward compat with existing data
  totalEnabledPolicies: number
  policyAnalysis: PolicyAnalysisItem[]
  requiredConditions: string[]
  conditionLabels: Record<string, string>
}

export interface PolicyAnalysisItem {
  name: string
  id: string
  checks: Record<string, boolean>
  passedCount: number
  totalChecks: number
}

// Settings-based diagnostics (for boolean, string, number checks)
export interface SettingCheckDiagnostics {
  type: 'settingChecks'
  settingChecks: SettingCheckItem[]
}

export interface SettingCheckItem {
  setting: string           // Machine-readable key
  label: string             // Human-readable label
  expected: unknown         // Expected value
  actual: unknown           // Actual value found
  passed: boolean           // Whether check passed
  context?: string          // Optional context (e.g., which remote domain)
}

// Item list diagnostics (for user/resource lists)
export interface ItemListDiagnostics {
  type: 'itemList'
  label: string
  items: Array<{ name: string; detail?: string }>
  totalCount: number
}

// Data unavailable diagnostics (for license-gated or missing data)
export interface DataUnavailableDiagnostics {
  type: 'dataUnavailable'
  reason: string
  dataPath: string
  licenseRequired?: {
    name: string
    description: string
    infoUrl?: string
  }
}

// Union type for all diagnostic formats
export type DiagnosticsData = PolicyCheckDiagnostics | SettingCheckDiagnostics | ItemListDiagnostics | DataUnavailableDiagnostics

// Score history for trends
export interface ComplianceScoreHistory {
  id: string
  tenantId: string
  standardId?: string             // null = overall score
  standardCode?: string
  snapshotDate: string
  score: number
  passedControls: number
  totalControls: number
}

// Framework/Standard score summary for dashboard
// Maps to backend StandardScoreDto
export interface FrameworkScore {
  standardId: string
  standardCode: string        // Backend uses StandardCode
  standardName: string        // Backend uses StandardName
  rawScore: number            // Score without waivers
  adjustedScore: number       // Score with waivers factored
  weightedScore: number       // Criticality-weighted score
  weight: number
  passedControls: number
  failedControls: number
  waivedControls: number
  dataUnavailableControls: number
  /** Controls with pass/waiver + data-unavailable mix; count as passing in scores */
  partialControls: number
  totalControls: number
  lastEvaluatedAt?: string
  isEnabled: boolean
  includeInGuardianScore: boolean
}

// Workload score breakdown for Guardian framework
export interface WorkloadScore {
  name: string
  violations: number
  critical: number
  controls: number
  passed: number
  color: string
  icon: string
}

// Convenience alias for code that uses old property names
// Use for backward compatibility during transition
export interface FrameworkScoreDisplay {
  standardId: string
  code: string              // Alias for standardCode
  name: string              // Alias for standardName
  score: number             // Canonical BPA score: weightedScore (criticality-weighted) — feeds Pillar tile, brief, Guardian Score, per-framework UIs
  passedControls: number
  failedControls: number
  totalControls: number
  waivedControls: number
  dataUnavailableControls: number
  /** Controls with pass/waiver + data-unavailable mix; count as passing in scores */
  partialControls: number
  weight: number
  includeInGuardianScore: boolean
  lastEvaluatedAt?: string
  // Extended properties for dashboard V2
  description?: string
  violations?: number
  criticalViolations?: number
  isGuardian?: boolean
  workloadBreakdown?: WorkloadScore[]
  icon?: string
  color?: string
}

// Overall compliance summary
// Maps to backend ComplianceScoreDto
export interface ComplianceSummary {
  tenantId: string
  overallScore: number
  totalPassedControls: number
  totalFailedControls: number
  totalWaivedControls: number
  totalControls: number
  lastEvaluatedAt?: string
  standardScores: FrameworkScore[]  // Backend uses StandardScores

  // Computed/derived fields for UI convenience (not from backend)
  // These should be calculated client-side from standardScores
  totalViolations?: number
  criticalViolations?: number
  highViolations?: number
  mediumViolations?: number
  evaluationDurationMs?: number

  // Alias for backward compatibility with UI code
  // Maps to standardScores
  frameworkScores?: FrameworkScoreDisplay[]

  // Collection health from backend evaluation (Track C)
  collectionHealth?: WorkloadCollectionHealth[]
}

// Compliance trend data point
export interface ComplianceTrendPoint {
  date: string
  overallScore: number
  frameworkScores: {
    code: string
    score: number
  }[]
}

/**
 * One row per re-evaluation event. Mirrors `ScoreEventDto` from the backend;
 * newest-first ordering and the server-computed `delta` (null for the oldest
 * event in window) are part of the contract — consumers must not re-sort or
 * recompute.
 */
export interface ScoreEvent {
  id: string
  evaluatedAt: string
  score: number
  delta: number | null
  triggeredBy: string | null
  passedControls: number
  totalControls: number
}

/**
 * Categorical change type for a control flip row. Drives the verb and chip
 * color in the "Recent control moves" feed. Matches the backend's
 * `ControlChangeDto.ChangeKind` string-union contract.
 */
export type ControlChangeKind = 'Improved' | 'Regressed' | 'Waived' | 'WaiverLapsed'

/**
 * One row in the BPA "Recent control moves" feed — a single policy whose
 * status flipped (Pass↔Fail, or had a waiver applied/lapse) since the prior
 * evaluation. Mirrors `ControlChangeDto` from the backend; the server orders
 * newest-first and computes `scoreDelta` per-row, so consumers must not
 * re-sort or recompute.
 */
export interface ControlChange {
  /** Underlying `ComplianceControlChanges.Id` (stable row identifier). */
  id: string
  /** When the flip was detected (ISO 8601). */
  detectedAt: string
  /** Policy code, e.g. "MS.AAD.1.1v1". */
  policyCode: string
  /** Human-readable policy title rendered as the row headline. */
  policyTitle: string
  /** Security category (e.g. "Identity", "Exchange") from CompliancePolicies. */
  category?: string | null
  /** Target M365 product (e.g. "EntraID", "Exchange") from CompliancePolicies. */
  product?: string | null
  /** Status before the flip: Pass | Fail | Waived. */
  previousStatus: string
  /** Status after the flip. */
  newStatus: string
  /** Categorical change type — drives chip color and verb selection. */
  changeKind: ControlChangeKind
  /**
   * Signed Guardian Score delta in percentage points. Positive = score moved
   * up. Null when the policy is unweighted or has no starred-framework
   * mapping — the UI renders "Not scored" in that case.
   */
  scoreDelta: number | null
  /**
   * First affected resource label (e.g. "Conditional Access Policy:
   * MFA-All-Users"). Null when the policy has no resource scope or when many
   * resources are affected.
   */
  affectedResource?: string | null
  /**
   * Count of affected resources for this policy in the new state. Drives the
   * optional "(N resources)" suffix.
   */
  affectedResourceCount: number
  /**
   * User email/ObjectId responsible for the flip. For eval-triggered flips,
   * the user who ran the evaluation. For waiver-driven flips
   * (`changeKind === 'Waived'`), the waiver creator. Null for scheduled
   * evals — the UI renders "Detected by scheduled scan".
   */
  triggeredBy?: string | null
}

// =============================================================================
// Collection Health Types (from Track C backend)
// =============================================================================

export interface CollectionHealth {
  graphCollected: boolean
  powerShellCollected: boolean
  /** Whether Graph API data is expected for this workload (e.g. false would be unusual) */
  graphExpected: boolean
  /** Whether PowerShell data is expected for this workload (false for Identity, which is Graph-only) */
  powerShellExpected: boolean
  graphCollectedAt: string | null
  powerShellCollectedAt: string | null
  errors: string[]
}

export interface WorkloadCollectionHealth {
  workload: string
  health: CollectionHealth
}

// Violation summary from dedicated summary endpoint (Track A)
export interface ViolationSummaryResponse {
  tenantId: string
  open: number
  waived: number
  resolved: number
  pass: number
  byCriticality: Record<string, number>
  byFramework: Record<string, number>
  byWorkload: Record<string, number>
  totalControls: number
  collectionHealth?: WorkloadCollectionHealth[]
}

// API response types
export interface ComplianceScoresResponse {
  summary: ComplianceSummary
  evaluations: ComplianceEvaluation[]
}

export interface ComplianceViolationsResponse {
  items: ComplianceViolation[]
  total: number
  pageNumber: number   // Maps to backend PageNumber
  pageSize: number     // Maps to backend PageSize
  totalPages: number
}

export interface ComplianceHistoryResponse {
  history: ComplianceScoreHistory[]
  trends: ComplianceTrendPoint[]
}

// Waiver request payload
export interface CreateWaiverRequest {
  policyId: string
  waiverType: WaiverType
  reason: string
  expiresAt?: string
}

export interface UpdateWaiverRequest {
  waiverType?: WaiverType
  reason?: string
  expiresAt?: string
}

// Standards configuration request
export interface UpdateStandardConfigRequest {
  standardId: string
  isEnabled: boolean
  weight: number
  includeInGuardianScore: boolean
}

// API request types for compliance operations

// Assign a standard to a tenant
export interface AssignStandardRequest {
  standardId: string
  weight: number
  includeInGuardianScore: boolean
}

// Update tenant standard settings
export interface UpdateTenantStandardRequest {
  isEnabled: boolean
  weight: number
  includeInGuardianScore: boolean
}

// Trigger evaluation request (can be empty or include options)
export interface TriggerEvaluationRequest {
  standardIds?: string[]  // If not specified, evaluate all assigned standards
  forceRefresh?: boolean  // Force re-collection of tenant data
}

// Violation filter parameters
// Maps to backend ViolationFilterDto
export interface ViolationFilters {
  status?: ViolationStatus
  criticality?: PolicyCriticality
  category?: string
  product?: string
  standardId?: string
  // When false, the request explicitly opts in to violations from unstarred
  // frameworks. When undefined, the param is omitted and the server default
  // (starred-only) applies. Set true is unnecessary — the server default
  // covers it — but is supported for clarity.
  onlyStarred?: boolean
  pageNumber?: number  // Backend uses PageNumber (default 1)
  pageSize?: number    // Backend uses PageSize (default 50)
}

// OPA service health status
// Maps to backend OpaHealthDto
export interface OpaHealthStatus {
  isHealthy: boolean
  opaVersion: string
  checkedAt: string
  loadedBundles: string[]
  errorMessage?: string
}

// Evaluation result returned after triggering evaluation
export interface EvaluationResult {
  evaluationId: string
  tenantId: string
  status: 'Completed' | 'Partial' | 'Failed' | 'NoStandards'
  evaluatedAt: string
  duration: number
  standardsEvaluated: number
  newViolationsFound: number
  resolvedViolations: number
  summary: ComplianceSummary
  message?: string
}

// Paginated response wrapper
// Note: Backend returns PageNumber/PageSize but API transforms to page/limit for frontend consistency
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  pageNumber: number    // Maps to backend PageNumber
  pageSize: number      // Maps to backend PageSize
  totalPages: number
}

// =============================================================================
// Workload Types for Dashboard Aggregation
// =============================================================================

/**
 * M365 Workload categories for grouping violations and controls.
 * These map product names to high-level workload categories.
 */
export const M365_WORKLOADS = [
  'Identity',
  'Exchange',
  'Teams',
  'SharePoint',
  'Defender',
  'Other'
] as const
export type M365Workload = typeof M365_WORKLOADS[number]

/**
 * Mapping from product/category names to workload categories.
 * Used to aggregate violations across all frameworks by workload.
 */
export const PRODUCT_TO_WORKLOAD: Record<string, M365Workload> = {
  // Identity workload
  'Entra ID': 'Identity',
  'EntraID': 'Identity',
  'Azure AD': 'Identity',
  'AzureAD': 'Identity',
  'Identity': 'Identity',
  'Conditional Access': 'Identity',
  'ConditionalAccess': 'Identity',
  'Authentication': 'Identity',

  // Exchange workload
  'Exchange': 'Exchange',
  'ExchangeOnline': 'Exchange',
  'Exchange Online': 'Exchange',
  'EXO': 'Exchange',
  'Mail': 'Exchange',
  'Audit': 'Exchange',  // Audit logs are typically Exchange-related

  // Teams workload
  'Teams': 'Teams',
  'Microsoft Teams': 'Teams',
  'MicrosoftTeams': 'Teams',

  // SharePoint workload
  'SharePoint': 'SharePoint',
  'SharePointOnline': 'SharePoint',
  'SharePoint Online': 'SharePoint',
  'OneDrive': 'SharePoint',
  'SPO': 'SharePoint',

  // Defender workload
  'Defender': 'Defender',
  'Microsoft Defender': 'Defender',
  'MicrosoftDefender': 'Defender',
  'ATP': 'Defender',
  'MDO': 'Defender',
  'MDE': 'Defender',
  'Security': 'Defender',
}

/**
 * Summary of violations and controls per workload.
 * Used for the workload breakdown in the dashboard.
 */
export interface WorkloadSummary {
  workload: M365Workload
  violations: number      // Total violations across ALL frameworks for this workload
  critical: number        // Critical (SHALL) violations
  high: number            // High (SHOULD) violations
  medium: number          // Medium (MAY) violations
  controls: number        // Total controls evaluated
  passed: number          // Controls passing
}

/**
 * Per-workload BPA score served by the backend on the overall scores payload
 * (security-center-subpages-redesign §6.3 — Phase C). Mirrors the C# DTO
 * `IuvoGuardian.Core.DTOs.Compliance.WorkloadScore`. Drives the Health %
 * column on the BPA workload heatmap and the 7-day delta chip.
 */
export interface WorkloadScoreDisplay {
  workload: string          // "Identity" | "Exchange" | "Teams" | "SharePoint" | "Defender" | "Other"
  score: number             // 0..100, weighted pass-rate, one decimal
  totalControls: number     // in-scope controls (excludes data-unavailable)
  passedControls: number
  openShall: number
  openShould: number
  openMay: number
  dataUnavailable: number
  delta7d: number | null    // null when no comparable history row exists
}

/**
 * Enhanced dashboard response structure.
 * Combines overall scores with framework and workload breakdowns.
 */
export interface ComplianceDashboardData {
  // Overall metrics
  tenantId: string
  overallScore: number
  totalViolations: number
  criticalViolations: number
  highViolations: number
  mediumViolations: number
  totalControls: number
  passedControls: number
  waivedControls: number
  lastEvaluatedAt?: string

  // Framework breakdown (includes all frameworks with scores)
  frameworks: FrameworkScoreDisplay[]

  // Workload breakdown (aggregated across all frameworks)
  workloadSummary: WorkloadSummary[]

  // Per-workload BPA scores from the server (Phase C of §6.3). Optional —
  // older payloads pre-deploy will not carry this and the BPA tab falls back
  // to client-computed counts (no Health column).
  workloadScores?: WorkloadScoreDisplay[]
}

/**
 * Helper function to map a product/category name to a workload.
 * Returns 'Other' for unknown products.
 */
export function getWorkloadForProduct(product: string): M365Workload {
  if (!product) return 'Other'

  // Try direct match first
  if (product in PRODUCT_TO_WORKLOAD) {
    return PRODUCT_TO_WORKLOAD[product]
  }

  // Try case-insensitive match
  const normalizedProduct = product.toLowerCase().trim()
  for (const [key, workload] of Object.entries(PRODUCT_TO_WORKLOAD)) {
    if (key.toLowerCase() === normalizedProduct) {
      return workload
    }
  }

  // Check for partial matches
  if (normalizedProduct.includes('identity') || normalizedProduct.includes('entra') || normalizedProduct.includes('azure ad')) {
    return 'Identity'
  }
  if (normalizedProduct.includes('exchange') || normalizedProduct.includes('mail')) {
    return 'Exchange'
  }
  if (normalizedProduct.includes('team')) {
    return 'Teams'
  }
  if (normalizedProduct.includes('sharepoint') || normalizedProduct.includes('onedrive')) {
    return 'SharePoint'
  }
  if (normalizedProduct.includes('defender') || normalizedProduct.includes('security')) {
    return 'Defender'
  }

  return 'Other'
}

/**
 * Aggregate violations by workload.
 * Groups violations from all frameworks into workload categories.
 */
export function aggregateViolationsByWorkload(
  violations: ComplianceViolation[]
): WorkloadSummary[] {
  // Initialize workload summaries
  const workloadMap = new Map<M365Workload, WorkloadSummary>()
  for (const workload of M365_WORKLOADS) {
    workloadMap.set(workload, {
      workload,
      violations: 0,
      critical: 0,
      high: 0,
      medium: 0,
      controls: 0,
      passed: 0,
    })
  }

  // Aggregate violations
  for (const violation of violations) {
    // Use product field first, fall back to category
    const workload = getWorkloadForProduct(violation.product || violation.category)
    const summary = workloadMap.get(workload)!

    // Only count open violations (not waived or resolved)
    if (violation.status === 'Open') {
      summary.violations++

      // Count by criticality
      if (violation.criticality === 'SHALL') {
        summary.critical++
      } else if (violation.criticality === 'SHOULD') {
        summary.high++
      } else {
        summary.medium++
      }
    }

    // Each violation represents a failed control
    summary.controls++
  }

  // Return sorted by violation count (most issues first), excluding empty workloads
  return Array.from(workloadMap.values())
    .filter(ws => ws.violations > 0 || ws.controls > 0)
    .sort((a, b) => b.violations - a.violations)
}

/**
 * @deprecated Use aggregateViolationsByWorkload instead. With the server-side
 * summary endpoint (Track A), workload counts come from the backend directly.
 * Kept for backward compatibility - ignores frameworkScores parameter.
 */
export function calculateWorkloadSummaries(
  _frameworkScores: FrameworkScore[],
  violations: ComplianceViolation[]
): WorkloadSummary[] {
  return aggregateViolationsByWorkload(violations)
}
